import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, PrimaryCTA } from "@/components/ui-bits";
import PostContent from "@/components/PostContent";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return ""; }
}

export default function NewsPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/news/${slug}`)
      .then(({ data }) => setItem(data))
      .catch(() => navigate("/news"))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-32 text-ink-soft" data-testid="news-post-loading">Loading…</div>
      </Layout>
    );
  }
  if (!item) return null;

  return (
    <Layout>
      <SEO
        title={item.title}
        description={item.excerpt || `News from Shelton Law PLLC — ${item.title}`}
        path={`/news/${item.slug}`}
        ogType="article"
      />
      <article data-testid="news-post">
        <section className="border-b border-hairline">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-16 pb-12">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-soft hover:text-brass mb-10"
              data-testid="back-to-news"
            >
              <ArrowLeft className="w-3 h-3" strokeWidth={2} /> News
            </Link>
            <Eyebrow>{formatDate(item.created_at)}</Eyebrow>
            <h1 className="display-1 text-ink mt-5 leading-[1.1]">{item.title}</h1>
            <p className="mt-8 text-sm text-ink-soft tracking-wide">Shelton Law PLLC</p>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16 prose-law">
          <PostContent content={item.content} />
        </section>

        <section className="border-t border-hairline bg-ivory-alt">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 text-center">
            <Eyebrow>Inquiries</Eyebrow>
            <h3 className="font-serif text-3xl text-ink leading-tight mt-4 mb-8">
              Press or lateral inquiries may be directed to the firm.
            </h3>
            <div className="flex justify-center">
              <PrimaryCTA to="/contact" testId="news-post-cta">Contact the Firm</PrimaryCTA>
            </div>
          </div>
        </section>
      </article>
    </Layout>
  );
}
